import { useEffect, useRef, useState } from 'react';

/** Legacy `GameFrame.INTERACTION_TICK_MS` / `_SECONDS` (`:34-35`). */
const INTERACTION_TICK_MS = 30000;
const INTERACTION_TICK_SECONDS = 30;

export interface InteractionSeconds {
  /**
   * Ref for the game surface. Only interaction inside this element is counted,
   * so the rest of the page cannot accrue time on a module's behalf.
   */
  surfaceRef: (element: HTMLElement | null) => void;
  /**
   * Whether a game is on the clock. Driven by the game's own lifecycle rather
   * than the component's - see `useVifRulesModule`.
   */
  setActive: (active: boolean) => void;
  /** Seconds accumulated since the last call, resetting the counter. */
  take: () => number;
  /** Drop what has accumulated without reporting it. */
  reset: () => void;
}

/**
 * Measured activity, in seconds, for the `timeOnTask` field.
 *
 * Ports `GameFrame`'s interaction timer (`:637-652`): a 30-second tick that
 * only counts when the player actually touched the page during that window, so
 * an abandoned tab does not accrue time.
 *
 * Scope is explicit here in a way the canvas build never needed. There the
 * timer's owner *is* one game; a DOM board's hooks outlive any one game, and
 * the page around them is a real page rather than a canvas. Left window-wide
 * and always-on, the module's loading gap, its end screen, the practice
 * chooser and every unrelated click on the surrounding page would all land in
 * the next game's `timeOnTask`. So the counter sees only interaction inside the
 * game surface, and only while its owner says a game is running.
 */
export function useInteractionSeconds(): InteractionSeconds {
  const seconds = useRef(0);
  const interacted = useRef(false);
  const active = useRef(false);

  // The surface is held in state rather than a ref because it is mounted by a
  // later render than this hook's: the view shows a placeholder until the
  // player's position has been read, so an effect reading a ref on mount would
  // only ever find `null` and never listen to anything.
  const [surface, setSurface] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!surface) {
      return;
    }

    const onInteract = () => {
      if (active.current) {
        interacted.current = true;
      }
    };

    surface.addEventListener('pointerdown', onInteract);

    // One interval for the surface's whole lifetime. Rebuilding it whenever
    // the game starts or stops would restart the 30-second window each time
    // and quietly drop the part already served.
    const timer = window.setInterval(() => {
      if (active.current && interacted.current) {
        seconds.current += INTERACTION_TICK_SECONDS;
      }
      interacted.current = false;
    }, INTERACTION_TICK_MS);

    return () => {
      surface.removeEventListener('pointerdown', onInteract);
      window.clearInterval(timer);
    };
  }, [surface]);

  return useRef<InteractionSeconds>({
    surfaceRef: setSurface,
    setActive: (next: boolean) => {
      // Interaction from the stretch just ended must not be credited to the
      // window that follows it.
      if (!next) {
        interacted.current = false;
      }
      active.current = next;
    },
    take: () => {
      const value = seconds.current;
      seconds.current = 0;

      return value;
    },
    reset: () => {
      seconds.current = 0;
      interacted.current = false;
    },
  }).current;
}
